// Validation d'une animation JRAF, en trois couches (la 4e — justesse
// mathématique par le noyau — arrive avec l'animation de référence, lot 3) :
//
//   1. structure : taille, format / migrations, schéma Zod strict ;
//   2. cohérence : identifiants, références, expressions, calendrier, limites,
//      règles pédagogiques (narration prononçable, partie interactive) ;
//   3. version du lecteur exigée (`runtimeMin`).
//
// Le même code tourne dans le Studio (aperçu) et sur le serveur (publication).

import { collectDisplayTexts } from './display-text';
import {
  collectNumFields,
  evalNum,
  ExprError,
  fieldBound,
  objectBox,
  parameterScenarios,
  type Box,
} from './expressions';
import { frenchErrorMap, fromZodIssue, issue, type JrafIssue, type PathPart } from './issues';
import { JRAF_LIMITS as L } from './limits';
import { migrateJraf } from './migrate';
import { containsMath, splitSentences } from './narration';
import { checkPathData } from './path-data';
import { runtimeMinOf } from './runtime';
import { scheduleScene } from './schedule';
import { displayTextProblems, mathSegments, texProblems } from './tex';
import { formulaArithmeticErrors, transformArithmeticError } from './formula-check';
import { bookendNarrations, RESERVED_ID_PREFIX } from './bookends';
import { VOICE_MATCH_MIN, voiceMatch } from './voice';
import { checkConsistency } from './coherence';
import { checkLayout } from './layout';
import { checkPacing } from './pacing';
import { checkMentions } from './mentions';
import { compileAnimation, sceneNarrationTimings } from './timeline';
import {
  AUDIO_EXTENSIONS,
  HORA_KEYS,
  IMAGE_EXTENSIONS,
  jrafDocumentSchema,
  type JrafDocument,
} from './schema/document';
import { DRAWABLE_TYPES, TEXT_LIKE_TYPES, type JrafObject } from './schema/objects';
import { INTERACTIVE_ACTIONS, isInteractiveStep, stepTargets } from './schema/steps';

/**
 * Avertissements de QUALITÉ (mise en page, rythme) : l'animation se joue,
 * mais un élève verrait un défaut. La génération par l'IA les fait corriger
 * comme des erreurs (réparation), sans bloquer la publication.
 */
export const QUALITY_WARNING_CODES = [
  'layout_overlap',
  'layout_frame_tight',
  'layout_touch',
  'layout_crossed',
  'layout_outside',
  'layout_captions',
  'layout_under_panel',
  'pace_crowded',
  'pace_rushed',
  'pace_calc',
  'scene_short',
  'mention_missing',
  'hook_missing',
  'value_conflict',
  'figure_scale',
  'figure_label',
  'figure_claim',
  'voice_mismatch',
  'tex_number_format',
] as const;
export type QualityWarningCode = (typeof QUALITY_WARNING_CODES)[number];

export function isQualityWarning(issue: Pick<JrafIssue, 'code' | 'severity'>): boolean {
  return (
    issue.severity === 'warning' &&
    (QUALITY_WARNING_CODES as readonly string[]).includes(issue.code)
  );
}

export interface ValidateOptions {
  /** identifiants d'outils de la boîte à outils (catalogue de l'app) ; absent = non vérifié */
  toolIds?: Iterable<string>;
}

export type ValidationResult =
  | { ok: true; doc: JrafDocument; runtimeMin: number; warnings: JrafIssue[] }
  | { ok: false; errors: JrafIssue[]; warnings: JrafIssue[] };

export function validateJraf(input: unknown, opts: ValidateOptions = {}): ValidationResult {
  // ---- couche 1 : taille, format, schéma
  let bytes: number;
  try {
    bytes = new TextEncoder().encode(JSON.stringify(input) ?? '').length;
  } catch {
    // entrée non sérialisable (cycle, BigInt…) : jamais un JSON reçu du réseau
    return fail([issue([], 'not_json', 'Document illisible (JSON attendu).')]);
  }
  if (bytes > L.maxBytes) {
    return fail([
      issue(
        [],
        'too_large',
        `Animation trop lourde : ${Math.round(bytes / 1000)} Ko (${L.maxBytes / 1000} Ko au plus).`,
      ),
    ]);
  }
  const migrated = migrateJraf(input);
  if (!migrated.ok) return fail([issue([], migrated.code, migrated.message)]);
  const parsed = jrafDocumentSchema.safeParse(migrated.doc, { errorMap: frenchErrorMap });
  if (!parsed.success) {
    return fail(parsed.error.issues.map((z) => fromZodIssue(z, migrated.doc)));
  }
  const doc = parsed.data;

  // ---- couche 2 : cohérence
  const found = checkCoherence(doc, opts);
  const errors = found.filter((i) => i.severity === 'error');
  const warnings = found.filter((i) => i.severity === 'warning');
  if (errors.length) return { ok: false, errors, warnings };

  // ---- couche 3 : version du lecteur
  return { ok: true, doc, runtimeMin: runtimeMinOf(doc), warnings };
}

function fail(errors: JrafIssue[]): ValidationResult {
  return { ok: false, errors, warnings: [] };
}

export function checkCoherence(doc: JrafDocument, opts: ValidateOptions = {}): JrafIssue[] {
  const out: JrafIssue[] = [];
  const err = (path: PathPart[], code: string, message: string) =>
    out.push(issue(path, code, message));
  const warn = (path: PathPart[], code: string, message: string) =>
    out.push(issue(path, code, message, 'warning'));

  // ---------- identifiants uniques
  const uniq = (list: { id: string }[], base: PathPart[], what: string) => {
    const seen = new Set<string>();
    list.forEach((x, i) => {
      if (seen.has(x.id))
        err([...base, i, 'id'], 'duplicate_id', `${what} « ${x.id} » déjà utilisé`);
      seen.add(x.id);
    });
  };
  // préfixe réservé au lecteur (intro, générique : `withBookends`)
  const reserved = (id: string, path: PathPart[]) => {
    if (id.startsWith(RESERVED_ID_PREFIX))
      err(
        path,
        'reserved_id',
        `« ${id} » : le préfixe « ${RESERVED_ID_PREFIX} » est réservé au lecteur`,
      );
  };
  doc.objects.forEach((o, i) => reserved(o.id, ['objects', i, 'id']));
  doc.assets.forEach((a, i) => reserved(a.id, ['assets', i, 'id']));
  doc.scenes.forEach((s, si) => {
    reserved(s.id, ['scenes', si, 'id']);
    s.steps.forEach((st, ti) => reserved(st.id, ['scenes', si, 'steps', ti, 'id']));
  });
  uniq(doc.objects, ['objects'], 'objet');
  uniq(doc.scenes, ['scenes'], 'scène');
  uniq(doc.assets, ['assets'], 'ressource');
  uniq(doc.parameters, ['parameters'], 'paramètre');
  doc.scenes.forEach((s, si) => uniq(s.steps, ['scenes', si, 'steps'], 'étape'));

  // ---------- paramètres
  doc.parameters.forEach((p, i) => {
    if (p.type !== 'number') return;
    if (!(p.min < p.max))
      err(
        ['parameters', i, 'max'],
        'param_range',
        `« ${p.id} » : min doit être plus petit que max`,
      );
    else if (p.default < p.min || p.default > p.max)
      err(
        ['parameters', i, 'default'],
        'param_default',
        `« ${p.id} » : la valeur par défaut sort de [min ; max]`,
      );
    if (p.step !== undefined && p.step > p.max - p.min)
      err(['parameters', i, 'step'], 'param_step', `« ${p.id} » : le pas dépasse l'intervalle`);
  });
  const scenarios = parameterScenarios(doc.parameters);
  const [byDefault, ...others] = scenarios as [(typeof scenarios)[0], ...typeof scenarios];
  byDefault.errors.forEach((e) => err(['parameters', e.index, 'value'], 'expr_invalid', e.message));
  if (byDefault.errors.length === 0) {
    const reported = new Set<number>();
    for (const sc of others)
      for (const e of sc.errors) {
        if (reported.has(e.index)) continue;
        reported.add(e.index);
        err(['parameters', e.index, 'value'], 'expr_extreme', `${sc.label} : ${e.message}`);
      }
  }

  // ---------- ressources
  const assets = new Map(doc.assets.map((a) => [a.id, a]));
  /** une voix enregistrée dit-elle son texte ? (texte changé depuis, autre prise) */
  const checkVoice = (
    path: PathPart[],
    narration: { text: string; say?: string | undefined },
    voice: { durationMs: number; words?: { w: string; startMs: number; endMs: number }[] },
  ) => {
    const m = voiceMatch(narration, voice);
    if (m !== null && m < VOICE_MATCH_MIN)
      warn(
        path,
        'voice_mismatch',
        `la voix enregistrée ne dit pas ce texte (${Math.round(m * 100)} % des mots retrouvés) : réenregistre-la`,
      );
  };
  doc.assets.forEach((a, i) => {
    const ext = a.path.split('.').pop()?.toLowerCase() ?? '';
    const allowed: readonly string[] = a.kind === 'audio' ? AUDIO_EXTENSIONS : IMAGE_EXTENSIONS;
    if (!allowed.includes(ext))
      err(
        ['assets', i, 'path'],
        'asset_extension',
        `${a.kind === 'audio' ? 'audio' : 'image'} : extension .${ext} non autorisée (${allowed.join(', ')})`,
      );
    if (a.kind === 'audio' && a.words) {
      let last = 0;
      a.words.forEach((w, wi) => {
        const wp: PathPart[] = ['assets', i, 'words', wi];
        if (w.endMs < w.startMs) err(wp, 'word_cue', 'fin avant le début');
        else if (w.startMs < last) err(wp, 'word_cue', 'mots dans le désordre');
        else if (w.endMs > a.durationMs) err(wp, 'word_cue', 'mot au-delà de la fin de l’audio');
        last = Math.max(last, w.startMs);
      });
    }
  });

  // accroche de la vidéo : le mot mis en valeur est dans la phrase
  const hook = doc.bookends?.hook;
  if (hook?.emphasis && !hook.text.toLowerCase().includes(hook.emphasis.toLowerCase()))
    err(
      ['bookends', 'hook', 'emphasis'],
      'hook_emphasis',
      `« ${hook.emphasis} » n'est pas dans l'accroche « ${hook.text} »`,
    );
  if (!hook)
    warn(
      ['bookends'],
      'hook_missing',
      'pas d’accroche (bookends.hook) : la vidéo commencerait directement par « JeRaisonne présente »',
    );

  // voix de l'accroche, de l'intro et du générique
  for (const key of ['hookAudioAssetId', 'introAudioAssetId', 'outroAudioAssetId'] as const) {
    const id = doc.bookends?.[key];
    if (!id) continue;
    const a = assets.get(id);
    if (!a) err(['bookends', key], 'asset_unknown', `ressource « ${id} » inconnue`);
    else if (a.kind !== 'audio')
      err(['bookends', key], 'asset_kind', `« ${id} » n'est pas un audio`);
    else {
      const said = bookendNarrations(doc, 'video');
      const narration =
        key === 'hookAudioAssetId'
          ? said.hook
          : key === 'introAudioAssetId'
            ? said.intro
            : said.outro;
      if (narration) checkVoice(['bookends', key], narration, a);
    }
  }

  // ---------- objets
  const objects = new Map(doc.objects.map((o) => [o.id, o]));
  doc.objects.forEach((o, i) => {
    const base: PathPart[] = ['objects', i];
    if (o.parent !== undefined) {
      const parent = objects.get(o.parent);
      if (!parent) err([...base, 'parent'], 'parent_unknown', `groupe « ${o.parent} » inconnu`);
      else if (parent.type !== 'group')
        err([...base, 'parent'], 'parent_not_group', `« ${o.parent} » n'est pas un groupe`);
    }
    if (o.type === 'path') {
      const check = checkPathData(o.d);
      if (!check.ok) err([...base, 'd'], 'path_invalid', `chemin refusé : ${check.reason}`);
    }
    if (o.type === 'image') {
      const a = assets.get(o.asset);
      if (!a) err([...base, 'asset'], 'asset_unknown', `ressource « ${o.asset} » inconnue`);
      else if (a.kind !== 'image')
        err([...base, 'asset'], 'asset_kind', `« ${o.asset} » n'est pas une image`);
    }
    if (o.type === 'formula') {
      checkTex(o.tex, [...base, 'tex'], err);
      checkSmallFrac(o.tex, [...base, 'tex'], warn);
      checkTexNumbers(o.tex, [...base, 'tex'], warn);
    }
  });
  // profondeur et cycles des groupes
  doc.objects.forEach((o, i) => {
    let depth = 0;
    let cur: JrafObject | undefined = o;
    const seen = new Set<string>();
    while (cur?.parent !== undefined) {
      if (seen.has(cur.id)) {
        err(['objects', i, 'parent'], 'parent_cycle', 'les groupes se contiennent en boucle');
        return;
      }
      seen.add(cur.id);
      depth++;
      cur = objects.get(cur.parent);
    }
    if (depth > L.maxGroupDepth)
      err(
        ['objects', i, 'parent'],
        'group_too_deep',
        `groupes imbriqués sur plus de ${L.maxGroupDepth} niveaux`,
      );
  });

  // ---------- calculs justes (formules, transformations, formules des textes)
  const mathFalse = (path: PathPart[], message: string) =>
    err(path, 'math_false', `calcul faux : ${message}`);
  const texNow = new Map<string, string>();
  doc.objects.forEach((o, i) => {
    if (o.type !== 'formula') return;
    texNow.set(o.id, o.tex);
    for (const m of formulaArithmeticErrors(o.tex)) mathFalse(['objects', i, 'tex'], m);
  });
  doc.scenes.forEach((scene, si) =>
    scene.steps.forEach((step, ti) => {
      if (step.action !== 'formula.transform') return;
      const p: PathPart[] = ['scenes', si, 'steps', ti, 'to'];
      for (const m of formulaArithmeticErrors(step.to)) mathFalse(p, m);
      const from = texNow.get(step.target);
      if (from !== undefined) {
        const m = transformArithmeticError(from, step.to);
        if (m) mathFalse(p, m);
      }
      texNow.set(step.target, step.to);
    }),
  );
  for (const { path, text } of collectDisplayTexts(doc))
    for (const seg of mathSegments(text))
      for (const m of formulaArithmeticErrors(seg)) mathFalse(path, m);

  // ---------- textes affichés (sous-titres, titres, étiquettes, questions…)
  for (const { path, text } of collectDisplayTexts(doc)) {
    for (const seg of mathSegments(text)) checkTexNumbers(seg, path, warn);
    for (const problem of displayTextProblems(text)) {
      if (problem === 'unbalanced') err(path, 'math_unbalanced', 'un « $ » n’est pas refermé');
      else if (problem === 'display_math')
        err(
          path,
          'display_math',
          'pas de « $$ » : une formule à part va dans un objet « formula »',
        );
      else
        err(
          path,
          'tex_forbidden',
          'commande LaTeX refusée (liens, HTML, images, styles, définitions de macros)',
        );
    }
  }

  // ---------- distribution (dialogue) : rôles s1, s2… dans l'ordre, chaque scène dite par un rôle connu
  if (doc.cast) {
    doc.cast.forEach((r, ri) => {
      if (r.id !== `s${ri + 1}`)
        err(
          ['cast', ri, 'id'],
          'cast_order',
          `les rôles se suivent : « s1 », « s2 », « s3 » (attendu « s${ri + 1} »)`,
        );
    });
  }
  const roles = new Set((doc.cast ?? [{ id: 's1' }]).map((r) => r.id));
  doc.scenes.forEach((s, si) => {
    const who = s.narration.speaker;
    if (who && !roles.has(who))
      err(
        ['scenes', si, 'narration', 'speaker'],
        'speaker_unknown',
        `rôle « ${who} » absent de la distribution (« cast »)`,
      );
  });

  // ---------- partie interactive (payante)
  const firstInteractive = doc.scenes.findIndex((s) => s.interactive);
  if (firstInteractive >= 0) {
    if (!doc.metadata.cardId)
      err(
        ['metadata', 'cardId'],
        'interactive_without_card',
        'une partie interactive (payante) exige la SKILLCARD source : renseigne « cardId »',
      );
    if (firstInteractive === 0 && doc.scenes.every((s) => s.interactive))
      err(
        ['scenes'],
        'no_free_scene',
        'il faut au moins une scène gratuite avant la partie « À toi »',
      );
    doc.scenes.forEach((s, si) => {
      if (si > firstInteractive && !s.interactive)
        err(
          ['scenes', si, 'interactive'],
          'interactive_order',
          'les scènes « À toi » (interactives) viennent après toutes les scènes gratuites',
        );
    });
  }

  // ---------- scènes et étapes
  const toolIds = opts.toolIds ? new Set(opts.toolIds) : null;
  const shown = new Set<string>();
  doc.scenes.forEach((scene, si) => {
    const base: PathPart[] = ['scenes', si];
    const n = scene.narration;
    if (n.say === undefined && containsMath(n.text))
      err(
        [...base, 'narration', 'say'],
        'say_required',
        'la narration contient une formule : écris aussi « say », le texte que dit la voix (« a au carré »)',
      );
    if (n.say !== undefined && containsMath(n.say))
      err(
        [...base, 'narration', 'say'],
        'say_math',
        '« say » est lu à voix haute : pas de LaTeX ni de « $ »',
      );
    const sentences = splitSentences(n.text);
    if (n.say !== undefined && splitSentences(n.say).length !== sentences.length)
      err(
        [...base, 'narration', 'say'],
        'say_sentences',
        `« say » doit avoir autant de phrases que « text » (${sentences.length}) : une phrase = un sous-titre`,
      );
    if (n.audioAssetId) {
      const a = assets.get(n.audioAssetId);
      if (!a)
        err(
          [...base, 'narration', 'audioAssetId'],
          'asset_unknown',
          `ressource « ${n.audioAssetId} » inconnue`,
        );
      else if (a.kind !== 'audio')
        err(
          [...base, 'narration', 'audioAssetId'],
          'asset_kind',
          `« ${n.audioAssetId} » n'est pas un audio`,
        );
      else checkVoice([...base, 'narration', 'audioAssetId'], n, a);
    }

    let interactiveSteps = 0;
    const stepIds = new Set<string>();
    scene.steps.forEach((step, ti) => {
      const sp: PathPart[] = [...base, 'steps', ti];
      if (step.with !== undefined && step.atSentence !== undefined)
        err(
          sp,
          'anchor_conflict',
          'une étape démarre soit « with » une autre, soit « atSentence », pas les deux',
        );
      if (step.with !== undefined && !stepIds.has(step.with)) {
        const later = scene.steps.slice(ti + 1).some((s) => s.id === step.with);
        err(
          [...sp, 'with'],
          later ? 'with_not_before' : 'with_unknown',
          later
            ? `« with » doit désigner une étape PRÉCÉDENTE (« ${step.with} » vient après)`
            : `étape « ${step.with} » inconnue dans cette scène`,
        );
      }
      if (step.atSentence !== undefined && step.atSentence >= sentences.length)
        err(
          [...sp, 'atSentence'],
          'sentence_out_of_range',
          `la narration n'a que ${sentences.length} phrase(s) (0 à ${sentences.length - 1})`,
        );
      stepIds.add(step.id);

      if (isInteractiveStep(step)) {
        interactiveSteps++;
        if (!scene.interactive)
          err(
            [...sp, 'action'],
            'interactive_outside',
            `« ${step.action} » n'est permis que dans une scène « interactive » (partie « À toi »)`,
          );
      }

      for (const t of stepTargets(step)) {
        const o = objects.get(t);
        const tp = [...sp, 'target'];
        if (!o) {
          err(tp, 'target_unknown', `objet « ${t} » inconnu`);
          continue;
        }
        if (step.action === 'label.set' && o.type !== 'text' && o.type !== 'point')
          err(
            tp,
            'target_type',
            `label.set s'applique à un texte ou un point, pas à un « ${o.type} »`,
          );
        if (
          step.action === 'label.set' &&
          o.type === 'point' &&
          step.text.length > L.maxPointLabelChars
        )
          err(
            [...sp, 'text'],
            'label_too_long',
            `l'étiquette d'un point fait ${L.maxPointLabelChars} caractères au plus`,
          );
        if (step.action === 'formula.transform' && o.type !== 'formula')
          err(
            tp,
            'target_type',
            `formula.transform s'applique à une formule, pas à un « ${o.type} »`,
          );
        if (step.action === 'value.set' && o.type !== 'value')
          err(
            tp,
            'target_type',
            `value.set s'applique à un nombre (« value »), pas à un « ${o.type} »`,
          );
        if (step.action === 'object.show') {
          shown.add(t);
          if (step.effect === 'draw' && !DRAWABLE_TYPES.includes(o.type))
            err(
              [...sp, 'effect'],
              'effect_type',
              `l'effet « draw » trace un contour : pas pour un « ${o.type} »`,
            );
        }
      }

      switch (step.action) {
        case 'object.style':
          if (
            step.fill === undefined &&
            step.stroke === undefined &&
            step.strokeWidth === undefined &&
            step.opacity === undefined
          )
            err(
              sp,
              'style_empty',
              'object.style : indique au moins fill, stroke, strokeWidth ou opacity',
            );
          break;
        case 'formula.transform':
          checkTex(step.to, [...sp, 'to'], err);
          checkSmallFrac(step.to, [...sp, 'to'], warn);
          checkTexNumbers(step.to, [...sp, 'to'], warn);
          break;
        case 'question.ask': {
          if (step.correct >= step.choices.length)
            err(
              [...sp, 'correct'],
              'correct_out_of_range',
              `« correct » désigne le choix ${step.correct}, il n'y en a que ${step.choices.length} (0 à ${step.choices.length - 1})`,
            );
          const norm = step.choices.map((c) => c.replace(/\s+/g, ' ').trim().toLowerCase());
          if (new Set(norm).size !== norm.length)
            err([...sp, 'choices'], 'choices_duplicate', 'deux choix identiques');
          break;
        }
        case 'tool.open':
          if (toolIds && !toolIds.has(step.toolId))
            err(
              [...sp, 'toolId'],
              'tool_unknown',
              `outil « ${step.toolId} » absent de la boîte à outils`,
            );
          break;
      }
    });
    if (scene.interactive && interactiveSteps === 0)
      err(
        [...base, 'steps'],
        'interactive_empty',
        `une scène « À toi » contient au moins une question ou un outil (${INTERACTIVE_ACTIONS.join(', ')})`,
      );
  });

  // ---------- expressions : défaut, puis chaque valeur des curseurs et les coins
  for (const f of collectNumFields(doc)) {
    const bound = fieldBound(f);
    try {
      evalNum(f.value, byDefault.scope, bound);
    } catch (e) {
      const range = e instanceof ExprError && e.kind === 'range';
      err(f.path, range ? 'expr_out_of_range' : 'expr_invalid', (e as Error).message);
      continue;
    }
    if (typeof f.value === 'number') continue;
    for (const sc of others) {
      try {
        evalNum(f.value, sc.scope, bound);
      } catch (e) {
        err(f.path, 'expr_extreme', `${sc.label} : ${(e as Error).message}`);
        break;
      }
    }
  }
  const exprBroken = out.some((i) => i.code.startsWith('expr_') && i.severity === 'error');

  // ---------- tailles et canevas, pour chaque jeu de paramètres
  if (!exprBroken) {
    const badSize = new Set<string>();
    const outside = new Set<string>();
    for (const sc of scenarios) {
      const s = sc.scope;
      const where = sc === byDefault ? '' : ` (${sc.label})`;
      doc.objects.forEach((o, i) => {
        const p: PathPart[] = ['objects', i];
        if (!badSize.has(o.id)) {
          let problem: [PathPart[], string] | null = null;
          if (
            (o.type === 'rect' || o.type === 'image') &&
            (evalNum(o.width, s) <= 0 || evalNum(o.height, s) <= 0)
          )
            problem = [p, 'largeur et hauteur doivent être positives'];
          else if (o.type === 'rect' && o.rx !== undefined && evalNum(o.rx, s) < 0)
            problem = [[...p, 'rx'], 'l’arrondi « rx » ne peut pas être négatif'];
          else if (o.type === 'circle' && evalNum(o.r, s) <= 0)
            problem = [[...p, 'r'], 'le rayon doit être positif'];
          else if (o.type === 'ellipse' && (evalNum(o.rx, s) <= 0 || evalNum(o.ry, s) <= 0))
            problem = [p, 'les rayons doivent être positifs'];
          if (problem) {
            badSize.add(o.id);
            err(problem[0], 'negative_size', problem[1] + where);
          }
        }
        // textes, paramètres par défaut : mesurés par le contrôle de mise en page (ci-dessous)
        if (outside.has(o.id)) return;
        if (sc === byDefault && (TEXT_LIKE_TYPES as readonly string[]).includes(o.type)) return;
        const box = objectBox(o, s);
        if (!box) return;
        const off = groupOffset(o, objects, s);
        const b: Box = {
          minX: box.minX + off.x,
          maxX: box.maxX + off.x,
          minY: box.minY + off.y,
          maxY: box.maxY + off.y,
        };
        if (b.minX < 0 || b.minY < 0 || b.maxX > doc.canvas.width || b.maxY > doc.canvas.height) {
          outside.add(o.id);
          warn(
            ['objects', i],
            sc === byDefault ? 'outside_canvas' : 'outside_canvas_extreme',
            `« ${o.id} » sort du canevas${where}`,
          );
        }
      });
    }
  }

  // ---------- calendrier et durées
  let totalMs = 0;
  doc.scenes.forEach((scene, si) => {
    // mêmes débuts de phrases que le lecteur (voix enregistrée : à sa durée)
    const timings = sceneNarrationTimings(doc, scene);
    const sched = scheduleScene(scene, {
      sentenceStartsMs: timings.startsMs,
      narrationMs: timings.totalMs,
    });
    totalMs += sched.endMs;
    for (const h of sched.heldByGate) {
      const ti = scene.steps.findIndex((st) => st.id === h.stepId);
      warn(
        ['scenes', si, 'steps', ti],
        'anchor_before_gate',
        `« ${h.stepId} » est écrite après « ${h.gateId} » : elle attend la réponse de l'élève (son ancrage la plaçait avant)`,
      );
    }
    if (sched.timelineEndMs > L.maxSceneTimelineMs)
      err(
        ['scenes', si, 'steps'],
        'scene_too_long',
        `les étapes durent ${Math.round(sched.timelineEndMs / 1000)} s (${L.maxSceneTimelineMs / 1000} s au plus par scène)`,
      );
    else if (sched.endMs > L.maxSceneTimelineMs)
      warn(
        ['scenes', si],
        'scene_long',
        `scène d'environ ${Math.round(sched.endMs / 1000)} s avec la narration : longue pour un Short`,
      );
  });
  if (totalMs > L.maxTotalMs)
    err(
      ['scenes'],
      'animation_too_long',
      `animation d'environ ${Math.round(totalMs / 60000)} min (${L.maxTotalMs / 60000} min au plus)`,
    );

  // ---------- conseils (avertissements)
  doc.objects.forEach((o, i) => {
    if (o.initial?.visible === false && !shown.has(o.id))
      warn(
        ['objects', i],
        'object_never_shown',
        `« ${o.id} » est caché au départ et jamais montré`,
      );
  });
  // ---------- objets nommés par la voix (éclairés quand elle les dit)
  for (const mi of checkMentions(doc))
    warn(
      mi.objectIndex !== undefined
        ? ['objects', mi.objectIndex, 'mentions']
        : ['scenes', mi.sceneIndex ?? 0, 'narration', 'text'],
      mi.code,
      mi.message,
    );
  // ---------- mise en page et rythme (document sans erreur : il se compile)
  if (!out.some((i) => i.severity === 'error')) {
    try {
      const anim = compileAnimation(doc);
      const objectIndex = new Map(doc.objects.map((o, i) => [o.id, i]));
      for (const li of checkLayout(anim)) {
        const oi = objectIndex.get(li.objectId);
        warn(oi === undefined ? ['scenes', li.sceneIndex] : ['objects', oi], li.code, li.message);
      }
      for (const pi of checkPacing(anim))
        warn(
          pi.stepIndex === undefined
            ? ['scenes', pi.sceneIndex]
            : ['scenes', pi.sceneIndex, 'steps', pi.stepIndex],
          pi.code,
          pi.message,
        );
      // cohérence : figure, nombres, voix et questions disent la même chose
      for (const ci of checkConsistency(anim)) {
        const oi = ci.objectId === undefined ? undefined : objectIndex.get(ci.objectId);
        const path: PathPart[] =
          oi !== undefined
            ? ['objects', oi]
            : ci.stepIndex !== undefined
              ? ['scenes', ci.sceneIndex, 'steps', ci.stepIndex]
              : ['scenes', ci.sceneIndex];
        // un nombre arrondi en silence est faux : erreur
        if (ci.code === 'value_rounded') err(path, ci.code, ci.message);
        else warn(path, ci.code, ci.message);
      }
    } catch (e) {
      warn([], 'layout_unchecked', `mise en page non contrôlée : ${(e as Error).message}`);
    }
  }

  const ratio = doc.canvas.width / doc.canvas.height;
  if (Math.abs(ratio - 9 / 16) > 0.01)
    warn(['canvas'], 'canvas_ratio', 'canevas pas en 9:16 : la vidéo Short sera recadrée');
  const horas = doc.scenes.map((s) => s.hora);
  if (horas.some((h) => h !== undefined)) {
    const missing = HORA_KEYS.filter((k) => !horas.includes(k));
    if (missing.length)
      warn(['scenes'], 'hora_missing', `méthode HORA incomplète : il manque ${missing.join(', ')}`);
    let last = -1;
    horas.forEach((h, si) => {
      if (h === undefined) return;
      const rank = HORA_KEYS.indexOf(h);
      if (rank < last)
        warn(
          ['scenes', si, 'hora'],
          'hora_order',
          `« ${h} » vient après une étape plus avancée de la méthode`,
        );
      last = Math.max(last, rank);
    });
  } else if (doc.metadata.cardId) {
    warn(
      ['scenes'],
      'hora_absent',
      'animation tirée d’une SKILLCARD : indique l’étape HORA de chaque scène',
    );
  }
  return out;
}

function checkTex(
  tex: string,
  path: PathPart[],
  err: (p: PathPart[], c: string, m: string) => void,
) {
  for (const problem of texProblems(tex)) {
    if (problem === 'forbidden')
      err(
        path,
        'tex_forbidden',
        'commande LaTeX refusée (liens, HTML, images, styles, définitions de macros)',
      );
    else err(path, 'tex_dollar', 'une formule s’écrit sans « $ »');
  }
}

/** `\frac` dans une formule est rendu en ligne (petit) : `\dfrac` se lit sur un téléphone. */
const INLINE_FRAC = /\\frac(?![a-zA-Z])/;

function checkSmallFrac(
  tex: string,
  path: PathPart[],
  warn: (p: PathPart[], c: string, m: string) => void,
) {
  if (INLINE_FRAC.test(tex))
    warn(
      path,
      'tex_small_frac',
      'fraction rendue petite dans une formule : écris \\dfrac au lieu de \\frac',
    );
}

/**
 * Nombres en formule : la virgule décimale seule est rendue « 1, 03 » (ponctuation) et
 * l'espace des milliers disparaît (« 1 000 » → « 1000 ») : `1{,}03`, `1\,000`.
 */
const TEX_DECIMAL_COMMA = /\d,\d/;
const TEX_THOUSANDS_SPACE = /\d \d{3}(?!\d)/;

function checkTexNumbers(
  tex: string,
  path: PathPart[],
  warn: (p: PathPart[], c: string, m: string) => void,
) {
  if (TEX_DECIMAL_COMMA.test(tex))
    warn(
      path,
      'tex_number_format',
      `virgule décimale en formule (« ${tex.slice(0, 40)} ») : écris 1{,}03, sinon elle est rendue « 1, 03 »`,
    );
  else if (TEX_THOUSANDS_SPACE.test(tex))
    warn(
      path,
      'tex_number_format',
      `espace des milliers en formule (« ${tex.slice(0, 40)} ») : écris 1\\,000, sinon il disparaît (1000)`,
    );
}

function groupOffset(
  o: JrafObject,
  objects: Map<string, JrafObject>,
  scope: Record<string, number>,
) {
  let x = 0;
  let y = 0;
  let cur = o.parent !== undefined ? objects.get(o.parent) : undefined;
  let guard = 0;
  while (cur && cur.type === 'group' && guard++ < L.maxGroupDepth + 1) {
    x += evalNum(cur.x, scope);
    y += evalNum(cur.y, scope);
    cur = cur.parent !== undefined ? objects.get(cur.parent) : undefined;
  }
  return { x, y };
}
